// src/services/firebase.js
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

// This app's own project (ddd-relationship-compass). It holds partner
// sessions (sharedAssessments), which partners join with a code and no
// account. Nobody's saved work lives here any more: that is backed up to
// their Dive Deep Dialogues account in the Auth Hub (below).
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase - check if already initialized to prevent duplicate app error
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// DDD Auth Hub: the account a person uses across the Dive Deep Dialogues
// apps. Sign-in and cloud backup both run on this second Firebase app, so
// when someone deletes their DDD account the hub deletes their Relationship
// Compass backup with it (users/{uid}/apps/relationship-compass in the hub).
// Same set-up as MindEase (apps/mindease/src/services/config/firebase.js).
export const useAuthHub = import.meta.env.VITE_USE_AUTH_HUB === 'true';
const authHubConfig = {
  apiKey: import.meta.env.VITE_AUTH_HUB_API_KEY,
  authDomain: 'ddd-auth-hub-4ed14.firebaseapp.com',
  projectId: import.meta.env.VITE_AUTH_HUB_PROJECT_ID || 'ddd-auth-hub-4ed14',
};
let authHubApp = null;
let authHubAuth = null;
if (useAuthHub && authHubConfig.apiKey) {
  try {
    authHubApp = initializeApp(authHubConfig, 'auth-hub');
    authHubAuth = getAuth(authHubApp);
  } catch (error) {
    console.error('Auth Hub initialisation error:', error);
    authHubApp = null;
    authHubAuth = null;
  }
} else if (import.meta.env.DEV) {
  console.warn('Auth Hub not configured (VITE_USE_AUTH_HUB / VITE_AUTH_HUB_API_KEY): the app runs signed out and saves on this device only.');
}
export { authHubApp, authHubAuth };

// Emulator builds for the audit gates only (docs/audit-scripts/README.md).
// VITE_USE_EMULATORS is never set for a real build, so this whole block is
// dropped from production bundles. The hub gets its own Auth emulator (:9199)
// because, as in production, its accounts are separate from this project's.
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  if (authHubAuth) connectAuthEmulator(authHubAuth, 'http://127.0.0.1:9199', { disableWarnings: true });
}

// No Firebase Analytics or Performance Monitoring: usage analytics runs only
// through the consent-aware @ddd/analytics rail (src/services/analytics.js),
// which sends nothing until the user says yes.

export default app;
